import {useRef, useState} from 'react'
import isEqual from 'lodash/isEqual'
import type {Moment} from 'moment-timezone'
import type {ParserT} from '@freckle/parser'
import {Parser, number, string, array, literal, record} from '@freckle/parser'
import {useSafeEffectExtraDeps} from '@freckle/react-hooks'
import type {SubjectT} from '@freckle/educator-entities/ts/common/helpers/subjects'
import {CenteredSpinner} from '@freckle/educator-materials/ts/components/spinner'
import ApiHelper from '@freckle/school/ts/common/helpers/api-helper'
import type {
  AllProvidersOrSpecificT,
  PracticeTypeFilterT
} from '@freckle/school/ts/common/helpers/api-helper'
import {embedContainer, periscopeEmbed} from './periscope-embed.module.scss'

type SchoolUsageData = {
  tag: 'school_usage'
  schoolId: number | undefined | null
  grade: number | undefined | null
  idpOrigins: AllProvidersOrSpecificT
  practiceTypes: PracticeTypeFilterT
  startDate: Moment
  endDate: Moment
}

type DistrictGrowthData = {
  tag: 'district_growth'
  subject: SubjectT
  idpOrigins: AllProvidersOrSpecificT
  startDate: Moment
  endDate: Moment
  grade: number | undefined | null
  domain: string | undefined | null
}

type SchoolGrowthData = {
  tag: 'school_growth'
  subject: SubjectT
  schoolId: number
  idpOrigins: AllProvidersOrSpecificT
  startDate: Moment
  endDate: Moment
  grade: number | undefined | null
  domain: string | undefined | null
}

type TeacherGrowthData = {
  tag: 'teacher_growth'
  subject: SubjectT
  teacherId: number
  idpOrigins: AllProvidersOrSpecificT
  startDate: Moment
  endDate: Moment
  grade: number | undefined | null
  domain: string | undefined | null
}

type StudentGrowthData = {
  tag: 'student_growth'
  subject: SubjectT
  studentId: number
  idpOrigins: AllProvidersOrSpecificT
  startDate: Moment
  endDate: Moment
  domain: string | undefined | null
}

export type PeriscopeEmbedData =
  SchoolUsageData | DistrictGrowthData | SchoolGrowthData | TeacherGrowthData | StudentGrowthData

type PeriscopeDrilldownPostMessageData = {
  event_type: 'drilldown'
  destination_dashboard_id: number
  filter_values: Array<DrilldownFilter>
}

type DrilldownFilter = {
  filter_name: string
  column_value: string
}

const parseDrilldownFilter: ParserT<DrilldownFilter> = record({
  filter_name: string(),
  column_value: string()
})

const parseDrilldownPostMessageData: ParserT<PeriscopeDrilldownPostMessageData> = record({
  event_type: literal('drilldown'),
  destination_dashboard_id: number(),
  filter_values: array(parseDrilldownFilter)
})

type Props = {
  data: PeriscopeEmbedData
  onDrilldown?: (filters: {[filter: string]: string}) => void
}

export function PeriscopeEmbed(props: Props) {
  const {data, onDrilldown} = props
  const oldData = useRef(data)
  const [embedUrl, setEmbedUrl] = useState<string | null>(null)

  useSafeEffectExtraDeps<{
    props: Props
    fetchEmbedUrl: (props: Props) => void
    handlePostMessage: (event: MessageEvent) => void
  }>(
    ({props, fetchEmbedUrl, handlePostMessage}) => {
      fetchEmbedUrl(props)
      window.addEventListener('message', handlePostMessage)
      return () => {
        window.removeEventListener('message', handlePostMessage)
      }
    },
    [],
    {
      props: {value: props, comparator: isEqual},
      fetchEmbedUrl: {value: fetchEmbedUrl, comparator: () => true},
      handlePostMessage: {value: handlePostMessage, comparator: () => true}
    }
  )

  useSafeEffectExtraDeps<{
    data: PeriscopeEmbedData
    props: Props
    fetchEmbedUrl: (props: Props) => void
  }>(
    ({data, props, fetchEmbedUrl}) => {
      if (!isEqual(data, oldData.current)) {
        fetchEmbedUrl(props)
        oldData.current = data
      }
    },
    [],
    {
      data: {value: data, comparator: isEqual},
      props: {value: props, comparator: isEqual},
      fetchEmbedUrl: {value: fetchEmbedUrl, comparator: () => true}
    }
  )

  function fetchEmbedUrl(props: Props) {
    const promise = getPeriscopeEmbedPromise(props.data) as Promise<{embedUrl: string}>

    setEmbedUrl(null)

    promise.then(({embedUrl}) => {
      setEmbedUrl(embedUrl)
    })
  }

  function handlePostMessage(event: MessageEvent) {
    if (event.data === null || event.data === undefined) return

    /* N.B. We cast event.data to any so we can extract event_type without
    parsing. We only care about drilldowns, so we don't want to enumerate all
    event types and fail if we don't recognize one. */
    const eventType: string = (event.data as {event_type: string}).event_type
    switch (eventType) {
      case 'drilldown':
        const eventData = Parser.run(event.data, parseDrilldownPostMessageData)
        const filters = eventData.filter_values
        const filterObject = Object.fromEntries(
          filters.map(filter => [filter.filter_name, filter.column_value])
        )
        if (onDrilldown !== undefined) onDrilldown(filterObject)
        break
      default:
        break
    }
  }
  return embedUrl === null ? (
    <CenteredSpinner />
  ) : (
    <div className={embedContainer}>
      <embed className={periscopeEmbed} src={embedUrl} key={embedUrl} />
    </div>
  )
}

function getPeriscopeEmbedPromise(data: PeriscopeEmbedData): Promise<unknown> {
  switch (data.tag) {
    case 'school_usage':
      return ApiHelper.getPeriscopeEmbedSchoolsUsage(
        data.schoolId,
        data.grade,
        data.idpOrigins,
        data.practiceTypes,
        data.startDate,
        data.endDate
      )
    case 'district_growth':
      return ApiHelper.getPeriscopeEmbedDistrictGrowth(
        data.subject,
        data.idpOrigins,
        data.startDate,
        data.endDate,
        data.grade,
        data.domain
      )
    case 'school_growth':
      return ApiHelper.getPeriscopeEmbedSchoolGrowth(
        data.subject,
        data.schoolId,
        data.idpOrigins,
        data.startDate,
        data.endDate,
        data.grade,
        data.domain
      )
    case 'teacher_growth':
      return ApiHelper.getPeriscopeEmbedTeacherGrowth(
        data.subject,
        data.teacherId,
        data.idpOrigins,
        data.startDate,
        data.endDate,
        data.grade,
        data.domain
      )
    case 'student_growth':
      return ApiHelper.getPeriscopeEmbedStudentGrowth(
        data.subject,
        data.studentId,
        data.idpOrigins,
        data.startDate,
        data.endDate,
        data.domain
      )
    default:
      throw new Error(`Invalid PeriscopeEmbed tag used: ${(data as {tag: never}).tag}`)
  }
}
